import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, SelectionList } from '@ygtechnologies/rubus-ui';
import { LoadingIndicator, classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faCircleCheck, faCircleXmark, faFileExport, faTag } from '@fortawesome/pro-light-svg-icons';
import { LinkingType, OptionGroupDto, RoomDto } from '@/types';
import { useRooms } from '@/services/rooms';
import { useSearchOptionGroups } from '@/services/options/option-groups.hooks';
import { optionGroupsKeys } from '@/services/options/option-groups.queryKeys';
import { setOptionGroupConfiguratorMapping } from '@/services/options/option-groups';
import { useOptionItemsByGroup } from '@/services/options/option-items.hooks';
import { getOptionItemsByGroup, setOptionItemConfiguratorMapping } from '@/services/options/option-items';
import {
    getConfiguratorMappingExportUrl,
    getConfiguratorValidationStructure,
    useConfiguratorValidationStructure,
} from '@/services/configurator-backends';
import { MappingValueSelect, MappingValueGroup } from './MappingValueSelect';
import theme from './Configurator3DMappingPage.module.scss';

type EditKind = 'group' | 'item';
interface EditEntry {
    kind: EditKind;
    id: number;
    value: string;
}

const keyOf = (kind: EditKind, id: number) => `${kind}:${id}`;
const normalize = (value: string) => (value.trim() === '' ? null : value.trim());

interface Configurator3DMappingPageProps {
    projectId: number;
}

export const Configurator3DMappingPage: React.FC<Configurator3DMappingPageProps> = ({ projectId }) => {
    const { data: groups, isLoading } = useSearchOptionGroups({ projectId });
    const { data: rooms } = useRooms(projectId);
    const { data: structure } = useConfiguratorValidationStructure(projectId);
    const queryClient = useQueryClient();

    // The only precondition for mapping is that an option group belongs to a room — so the rooms we
    // offer are those with at least one room-linked option group (not just configurator-assigned ones).
    const mappableRoomIds = useMemo(() => {
        const set = new Set<number>();
        (groups ?? []).forEach((g) => {
            if (g.linkType === LinkingType.Room) {
                g.linkedRooms.forEach((l) => set.add(l.roomId));
            }
        });
        return set;
    }, [groups]);

    // Left-hand menu entries: rooms with mappable option groups, ordered by their sort order then name.
    const mappableRooms = useMemo<RoomDto[]>(
        () =>
            [...(rooms ?? [])]
                .filter((r) => mappableRoomIds.has(r.id))
                .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
        [rooms, mappableRoomIds]
    );

    const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

    // Keep a valid room selected as the mappable rooms load/change.
    useEffect(() => {
        setSelectedRoomId((prev) =>
            prev != null && mappableRooms.some((r) => r.id === prev)
                ? prev
                : mappableRooms[0]?.id ?? null
        );
    }, [mappableRooms]);

    // Option groups linked to the selected room, ordered by name.
    const visibleGroups = useMemo<OptionGroupDto[]>(() => {
        if (selectedRoomId == null) return [];
        return (groups ?? [])
            .filter((g) => g.linkType === LinkingType.Room && g.linkedRooms.some((l) => l.roomId === selectedRoomId))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [groups, selectedRoomId]);

    // Every room-linked group — the scope the Validate button checks.
    const relevantGroups = useMemo<OptionGroupDto[]>(
        () => (groups ?? []).filter((g) => g.linkType === LinkingType.Room && g.linkedRooms.length > 0),
        [groups]
    );

    // A reachable configurator exposes params; that switches the page from free-text inputs
    // (workflow a) to assisted dropdowns (workflow b).
    const assisted = (structure?.paramNames.length ?? 0) > 0;
    const paramNames = structure?.paramNames ?? [];
    const optionNames = structure?.optionNames ?? [];
    const paramOptions = useMemo(() => {
        const map = new Map<string, string[]>();
        (structure?.params ?? []).forEach((p) => map.set(p.name, p.optionNames));
        return map;
    }, [structure]);
    // Group the group-dropdown's param names under their 3D configurator.
    const paramGroups = useMemo<MappingValueGroup[]>(
        () => (structure?.configurators ?? []).map((c) => ({ label: c.configuratorName, values: c.paramNames })),
        [structure]
    );

    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [edits, setEdits] = useState<Record<string, EditEntry>>({});
    const [validation, setValidation] = useState<Record<string, boolean>>({});
    const [validating, setValidating] = useState(false);
    const [validationRan, setValidationRan] = useState(false);

    const dirtyEntries = Object.values(edits);
    const validationValues = Object.values(validation);
    const checkedCount = validationValues.length;
    const invalidCount = validationValues.filter((v) => !v).length;

    const toggleExpand = (groupId: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });

    const setField = (kind: EditKind, id: number, value: string, original: string | null | undefined) => {
        const key = keyOf(kind, id);
        setEdits((prev) => {
            const next = { ...prev };
            if (value === (original ?? '')) {
                delete next[key];
            } else {
                next[key] = { kind, id, value };
            }
            return next;
        });
        // A previous validation marker is stale once the value changes.
        setValidation((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const getValue = (kind: EditKind, id: number, original: string | null | undefined) => {
        const key = keyOf(kind, id);
        return key in edits ? edits[key].value : original ?? '';
    };

    const isDirty = (kind: EditKind, id: number) => keyOf(kind, id) in edits;

    const validationResult = (kind: EditKind, id: number): boolean | undefined => validation[keyOf(kind, id)];

    const runValidation = async () => {
        setValidating(true);
        try {
            // Reuse the eagerly-loaded structure; fetch as a fallback if it hasn't arrived yet.
            const struct = structure ?? (await getConfiguratorValidationStructure(projectId));
            const paramSet = new Set(struct.paramNames);
            const optionSet = new Set(struct.optionNames);

            // Validate every group reachable from a configurator-assigned room (not just the
            // currently selected one). Fetch items for each so collapsed rows are validated too.
            const allGroups = relevantGroups;
            const itemsByGroup = await Promise.all(allGroups.map((g) => getOptionItemsByGroup(g.id)));

            const result: Record<string, boolean> = {};
            allGroups.forEach((g, idx) => {
                const groupValue = getValue('group', g.id, g.configuratorMappingId).trim();
                if (groupValue) {
                    result[keyOf('group', g.id)] = paramSet.has(groupValue);
                }
                (itemsByGroup[idx] ?? []).forEach((item) => {
                    const itemValue = getValue('item', item.id, item.configuratorMappingId).trim();
                    if (itemValue) {
                        result[keyOf('item', item.id)] = optionSet.has(itemValue);
                    }
                });
            });

            setValidation(result);
            setValidationRan(true);
        } finally {
            setValidating(false);
        }
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            await Promise.all(
                dirtyEntries.map((e) =>
                    e.kind === 'group'
                        ? setOptionGroupConfiguratorMapping(e.id, normalize(e.value))
                        : setOptionItemConfiguratorMapping(e.id, normalize(e.value))
                )
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionGroupsKeys.root });
            queryClient.invalidateQueries({ queryKey: ['getOptionItemsByGroup'] });
            setEdits({});
        },
    });

    if (isLoading) {
        return <LoadingIndicator />;
    }

    if (mappableRooms.length === 0) {
        return (
            <div className={theme.emptyState}>
                <Trans>No rooms have option groups to map yet.</Trans>
            </div>
        );
    }

    return (
        <div className={theme.container}>
            <div className={theme.layout}>
                <aside className={theme.sidebar}>
                    <SelectionList selectionMode="single">
                        {mappableRooms.map((room) => (
                            <SelectionList.Item
                                key={room.id}
                                label={
                                    <span className={theme.roomLabel}>
                                        <FontAwesomeIcon icon={room.icon ? ['fal', room.icon] : faTag} fixedWidth />
                                        {room.name}
                                    </span>
                                }
                                selected={room.id === selectedRoomId}
                                className={room.id === selectedRoomId ? theme.roomSelected : undefined}
                                onChange={() => setSelectedRoomId(room.id)}
                            />
                        ))}
                    </SelectionList>
                </aside>

                <div className={theme.content}>
                    <table className={classnames('table', theme.table)}>
                        <thead>
                            <tr>
                                <th className={theme.nameCol}>
                                    <Trans>Option group / item</Trans>
                                </th>
                                <th className={theme.typeCol}>
                                    <Trans>Type</Trans>
                                </th>
                                <th className={theme.mappingCol}>
                                    <Trans>Mapping ID</Trans>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleGroups.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className={theme.noGroups}>
                                        <Trans>No option groups are linked to this room.</Trans>
                                    </td>
                                </tr>
                            ) : (
                                visibleGroups.map((group) => (
                                    <MappingGroupRow
                                        key={group.id}
                                        group={group}
                                        expanded={expanded.has(group.id)}
                                        onToggle={() => toggleExpand(group.id)}
                                        getValue={getValue}
                                        setField={setField}
                                        isDirty={isDirty}
                                        validationResult={validationResult}
                                        assisted={assisted}
                                        paramNames={paramNames}
                                        paramGroups={paramGroups}
                                        optionNames={optionNames}
                                        paramOptions={paramOptions}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>

                    <div className={theme.footer}>
                        <div className={theme.footerLeft}>
                            <Button
                                variant="tertiary"
                                icon={faCircleCheck}
                                loading={validating}
                                disabled={relevantGroups.length === 0}
                                onClick={runValidation}>
                                <Trans>Validate</Trans>
                            </Button>
                            {validationRan &&
                                (invalidCount > 0 ? (
                                    <span className={theme.invalidText}>
                                        <Trans>
                                            {invalidCount} of {checkedCount} mapped values not found
                                        </Trans>
                                    </span>
                                ) : (
                                    <span className={theme.validText}>
                                        <Trans>All {checkedCount} mapped values found</Trans>
                                    </span>
                                ))}
                            <Button
                                as="a"
                                href={getConfiguratorMappingExportUrl(projectId)}
                                target="_blank"
                                variant="tertiary"
                                icon={faFileExport}>
                                <Trans>Download 3D Mapping</Trans>
                            </Button>
                        </div>
                        <div className={theme.footerRight}>
                            <Button variant="tertiary" disabled={dirtyEntries.length === 0 || saveMutation.isPending} onClick={() => setEdits({})}>
                                <Trans>Discard</Trans>
                            </Button>
                            <Button
                                variant="primary"
                                loading={saveMutation.isPending}
                                disabled={dirtyEntries.length === 0}
                                onClick={() => saveMutation.mutate()}>
                                <Trans>Save changes ({dirtyEntries.length})</Trans>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ValidationIcon: React.FC<{ result: boolean | undefined }> = ({ result }) => {
    if (result === undefined) return null;
    return (
        <FontAwesomeIcon
            icon={result ? faCircleCheck : faCircleXmark}
            className={result ? theme.valid : theme.invalid}
            title={result ? t`Found in configurator` : t`Not found in configurator`}
            fixedWidth
        />
    );
};

interface MappingGroupRowProps {
    group: OptionGroupDto;
    expanded: boolean;
    onToggle(): void;
    getValue(kind: EditKind, id: number, original: string | null | undefined): string;
    setField(kind: EditKind, id: number, value: string, original: string | null | undefined): void;
    isDirty(kind: EditKind, id: number): boolean;
    validationResult(kind: EditKind, id: number): boolean | undefined;
    assisted: boolean;
    paramNames: string[];
    paramGroups: MappingValueGroup[];
    optionNames: string[];
    paramOptions: Map<string, string[]>;
}

const MappingGroupRow: React.FC<MappingGroupRowProps> = ({
    group,
    expanded,
    onToggle,
    getValue,
    setField,
    isDirty,
    validationResult,
    assisted,
    paramNames,
    paramGroups,
    optionNames,
    paramOptions,
}) => {
    const { data: items, isLoading } = useOptionItemsByGroup(expanded ? group.id : undefined);
    const groupResult = validationResult('group', group.id);

    // Item dropdowns are filtered to the options nested under the param the group maps to; if the
    // group's value isn't a recognized param (unset/custom), offer the full option list instead.
    const groupValue = getValue('group', group.id, group.configuratorMappingId).trim();
    const itemOptions = paramOptions.get(groupValue) ?? optionNames;

    return (
        <>
            <tr className={theme.groupRow}>
                <td className={theme.nameCol}>
                    <button type="button" className={theme.expandButton} onClick={onToggle} aria-label={t`Expand`}>
                        <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} fixedWidth />
                    </button>
                    <span className={theme.groupName}>{group.name}</span>
                </td>
                <td className={theme.typeCol}>
                    <Trans>Group</Trans>
                </td>
                <td className={theme.mappingCol}>
                    <div className={theme.mappingCell}>
                        {assisted ? (
                            <MappingValueSelect
                                value={getValue('group', group.id, group.configuratorMappingId)}
                                onChange={(value) => setField('group', group.id, value, group.configuratorMappingId)}
                                groups={paramGroups.length ? paramGroups : undefined}
                                options={paramNames}
                                placeholder={t`Mapping ID`}
                                dirty={isDirty('group', group.id)}
                                invalid={groupResult === false}
                            />
                        ) : (
                            <input
                                className={classnames('form-control', {
                                    [theme.dirty]: isDirty('group', group.id),
                                    [theme.invalidInput]: groupResult === false,
                                })}
                                value={getValue('group', group.id, group.configuratorMappingId)}
                                onChange={(e) => setField('group', group.id, e.target.value, group.configuratorMappingId)}
                                placeholder={t`Mapping ID`}
                            />
                        )}
                        <ValidationIcon result={groupResult} />
                    </div>
                </td>
            </tr>
            {expanded && isLoading && (
                <tr>
                    <td colSpan={3}>
                        <LoadingIndicator />
                    </td>
                </tr>
            )}
            {expanded &&
                (items ?? []).map((item) => {
                    const itemResult = validationResult('item', item.id);
                    return (
                        <tr key={item.id} className={theme.itemRow}>
                            <td className={classnames(theme.nameCol, theme.itemName)}>{item.product?.name}</td>
                            <td className={theme.typeCol}>
                                <Trans>Item</Trans>
                            </td>
                            <td className={theme.mappingCol}>
                                <div className={theme.mappingCell}>
                                    {assisted ? (
                                        <MappingValueSelect
                                            value={getValue('item', item.id, item.configuratorMappingId)}
                                            onChange={(value) => setField('item', item.id, value, item.configuratorMappingId)}
                                            options={itemOptions}
                                            placeholder={t`Mapping ID`}
                                            dirty={isDirty('item', item.id)}
                                            invalid={itemResult === false}
                                        />
                                    ) : (
                                        <input
                                            className={classnames('form-control', {
                                                [theme.dirty]: isDirty('item', item.id),
                                                [theme.invalidInput]: itemResult === false,
                                            })}
                                            value={getValue('item', item.id, item.configuratorMappingId)}
                                            onChange={(e) => setField('item', item.id, e.target.value, item.configuratorMappingId)}
                                            placeholder={t`Mapping ID`}
                                        />
                                    )}
                                    <ValidationIcon result={itemResult} />
                                </div>
                            </td>
                        </tr>
                    );
                })}
        </>
    );
};
